import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SEO from '../../components/SEO';
import Navbar from '../../component/Navbar/Navbar';
import Footer from '../../component/Footer/Footer';
import { useAuth } from '../../context/AuthContext';
import { getPostLoginPathFromUser } from '../../utils/postLoginRedirect';
import { api } from '../../api/client';
import { PasswordFieldToggle } from '../../components/PasswordFieldToggle';
import './login.css';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { login, user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorList, setErrorList] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [verifiedBanner, setVerifiedBanner] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState(null);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (searchParams.get('verified') === '1') {
      setVerifiedBanner(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!authLoading && user) {
      navigate(getPostLoginPathFromUser(user), { replace: true });
    }
  }, [authLoading, user, navigate]);

  const toErrorList = (err) => {
    const bag = err?.data?.errors;
    if (bag && typeof bag === 'object') {
      const messages = Object.values(bag).flat().filter(Boolean);
      return messages.length > 0 ? messages : [err?.message || 'Login failed.'];
    }
    return [err?.message || 'Login failed. Check your email and password.'];
  };

  const handleResendVerification = async () => {
    if (!email) return;
    setResendStatus(null);
    setResending(true);
    try {
      const data = await api.post('/auth/resend-verification-email', { email }, null);
      setResendStatus({ ok: true, message: data?.message || 'Check your inbox for the verification link.' });
    } catch (err) {
      setResendStatus({
        ok: false,
        message: err?.message || err?.data?.message || 'Could not resend. Try again later.',
      });
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorList([]);
    setFieldErrors({});
    setNeedsVerification(false);
    setResendStatus(null);
    setSubmitting(true);
    try {
      const me = await login(email, password);
      navigate(getPostLoginPathFromUser(me), { replace: true });
    } catch (err) {
      const verifyBlocked = err?.status === 403
        && String(err?.message || err?.data?.message || '').toLowerCase().includes('verify');
      if (verifyBlocked) {
        setNeedsVerification(true);
        setErrorList([
          err.message || err?.data?.message || 'Please verify your email before signing in.',
          'Did not get the email? Check spam, or resend the verification link below.',
        ]);
      } else {
        setErrorList(toErrorList(err));
      }
      if (err?.data?.errors && typeof err.data.errors === 'object') {
        const perField = {};
        Object.entries(err.data.errors).forEach(([key, messages]) => {
          if (Array.isArray(messages) && messages.length > 0) {
            perField[key] = messages[0];
          }
        });
        setFieldErrors(perField);
      }
      console.error('Login error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login">
      <SEO title="Login" description="Sign in to your Luxistt account to manage bookings and stays." path="/login" noindex />
      <Navbar />
      <div className="login-wrap">
        <div className="login-card">
          <h1 className="login-title">Welcome back</h1>
          <p className="login-subtitle">Sign in to manage your Luxistt bookings and upcoming stays.</p>
          {verifiedBanner && (
            <div className="login-success" role="status">
              Your email has been verified. You can sign in below.
            </div>
          )}
          {errorList.length > 0 && (
            <div className="login-error" role="alert">
              <ul className="login-error-list">
                {errorList.map((msg, idx) => <li key={`${msg}-${idx}`}>{msg}</li>)}
              </ul>
            </div>
          )}
          {needsVerification && (
            <div className="login-resend-verify">
              <button
                type="button"
                className="login-resend-verify-btn"
                onClick={handleResendVerification}
                disabled={resending || !email}
              >
                {resending ? 'Sending…' : 'Resend verification email'}
              </button>
              {resendStatus && (
                <p className={resendStatus.ok ? 'login-resend-verify-ok' : 'login-resend-verify-err'}>
                  {resendStatus.message}
                </p>
              )}
            </div>
          )}
          <form onSubmit={handleSubmit} className="login-form-fields">
            <label className="login-label" htmlFor="login-email">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className={`login-input${fieldErrors.email ? ' login-input-error' : ''}`}
            />
            {fieldErrors.email && <small className="login-field-error">{fieldErrors.email}</small>}
            <PasswordFieldToggle
              id="login-password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
              inputClassName={`login-input${fieldErrors.password ? ' login-input-error' : ''}`}
              errorText={fieldErrors.password}
            />
            <button type="submit" disabled={submitting} className="login-btn">
              {submitting ? (
                <span className="login-btn-spinner">
                  <span className="login-spinner" aria-hidden />
                  <span>Signing in…</span>
                </span>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
          <div className="login-meta-row">
            <Link to="/forgot-password" className="login-meta-link">Forgot password?</Link>
            <span className="login-meta-sep">•</span>
            <Link to="/signup" className="login-meta-link">Create account</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;
